const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Debug logging for API URL (remove in production)
console.log('API_URL configured as:', API_URL);
console.log('REACT_APP_API_URL env var:', process.env.REACT_APP_API_URL);

// Test connectivity function
export const testConnection = async () => {
  try {
    const response = await fetch(`${API_URL}/api/languages/`);
    console.log('Connection test status:', response.status);
    if (response.ok) {
      console.log('Backend connection successful');
      return true;
    } else {
      console.error('Backend connection failed:', response.status, response.statusText);
      return false;
    }
  } catch (error) {
    console.error('Backend connection error:', error);
    return false;
  }
};

export const fetchSayings = async (filters = {}, page = 1, size = 10) => {
  const queryParams = new URLSearchParams();
  
  if (filters.category_id) {
    queryParams.append('category_id', filters.category_id);
  }
  if (filters.language_id) {
    queryParams.append('language_id', filters.language_id);
  }
  if (filters.searchText) {
    queryParams.append('search', filters.searchText);
  }
  if (filters.approved !== undefined) {
    queryParams.append('approved', filters.approved);
  }
  if (filters.translation_language_id) {
    queryParams.append('translation_language_id', filters.translation_language_id);
  }
  
  // Add pagination parameters
  queryParams.append('page', page);
  queryParams.append('size', size);
  
  const response = await fetch(`${API_URL}/api/sayings/?${queryParams.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to fetch sayings');
  }
  return response.json();
};

export const fetchCategories = async () => {
  const res = await fetch(`${API_URL}/api/categories/`);
  if (!res.ok) {
    throw new Error('Failed to fetch categories');
  }
  return res.json();
};

export const fetchLanguages = async () => {
  const res = await fetch(`${API_URL}/api/languages/`);
  if (!res.ok) {
    throw new Error('Failed to fetch languages');
  }
  return res.json();
};

export const createSaying = async (saying) => {
  const res = await fetch(`${API_URL}/api/sayings/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(saying),
  });
  if (!res.ok) {
    throw new Error('Failed to create saying');
  }
  return res.json();
};

export const updateSaying = async (saying_id, saying) => {
  console.log('updateSaying called with:', { saying_id, saying });
  
  try {
    const res = await fetch(`${API_URL}/api/sayings/${saying_id}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(saying),
    });
    
    console.log('Response status:', res.status);
    
    if (!res.ok) {
      const errorData = await res.text();
      console.error('Error response:', errorData);
      throw new Error(`Failed to update saying: ${res.status} ${res.statusText} - ${errorData}`);
    }
    
    const result = await res.json();
    console.log('Update successful:', result);
    return result;
  } catch (error) {
    console.error('updateSaying error:', error);
    throw error;
  }
};

export const deleteSaying = async (saying_id) => {
  const res = await fetch(`${API_URL}/api/sayings/${saying_id}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error('Failed to delete saying');
  }
  return res.json();
};

// Translation API functions
export const fetchTranslations = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  
  if (filters.original_saying_id) {
    queryParams.append('original_saying_id', filters.original_saying_id);
  }
  if (filters.language_id) {
    queryParams.append('language_id', filters.language_id);
  }
  
  const response = await fetch(`${API_URL}/api/translations/?${queryParams.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to fetch translations');
  }
  return response.json();
};

export const createTranslation = async (translation) => {
  console.log('API: Creating translation:', translation);
  const res = await fetch(`${API_URL}/api/translations/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(translation),
  });
  
  console.log('API: Response status:', res.status);
  
  if (!res.ok) {
    const errorText = await res.text();
    console.error('API: Error response:', errorText);
    throw new Error(`Failed to create translation: ${res.status} ${res.statusText} - ${errorText}`);
  }
  
  const result = await res.json();
  console.log('API: Created translation:', result);
  return result;
};

export const updateTranslation = async (translation_id, translation) => {
  console.log('API: Updating translation:', translation_id, translation);
  const res = await fetch(`${API_URL}/api/translations/${translation_id}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(translation),
  });
  
  console.log('API: Response status:', res.status);
  
  if (!res.ok) {
    const errorText = await res.text();
    console.error('API: Error response:', errorText);
    throw new Error(`Failed to update translation: ${res.status} ${res.statusText} - ${errorText}`);
  }
  
  const result = await res.json();
  console.log('API: Updated translation:', result);
  return result;
};

export const deleteTranslation = async (translation_id) => {
  const res = await fetch(`${API_URL}/api/translations/${translation_id}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error('Failed to delete translation');
  }
  return res.json();
};

export async function createCategory(category) {
  const res = await fetch(`${API_URL}/api/categories/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(category),
  });
  if (!res.ok) {
    throw new Error('Failed to create category');
  }
  return res.json();
}

export async function updateCategory(category_id, category) {
  const res = await fetch(`${API_URL}/api/categories/${category_id}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(category),
  });
  if (!res.ok) {
    throw new Error('Failed to update category');
  }
  return res.json();
}

export async function deleteCategory(category_id) {
  const res = await fetch(`${API_URL}/api/categories/${category_id}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error('Failed to delete category');
  }
  return res.json();
}

export async function createLanguage(language) {
  const res = await fetch(`${API_URL}/api/languages/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(language),
  });
  if (!res.ok) {
    throw new Error('Failed to create language');
  }
  return res.json();
}

export async function updateLanguage(language_id, language) {
  const res = await fetch(`${API_URL}/api/languages/${language_id}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(language),
  });
  if (!res.ok) {
    throw new Error('Failed to update language');
  }
  return res.json();
}

export async function deleteLanguage(language_id) {
  const res = await fetch(`${API_URL}/api/languages/${language_id}/`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error('Failed to delete language');
  }
  return res.json();
}

export const fetchSayingById = async (sayingId) => {
  const response = await fetch(`${API_URL}/api/sayings/${sayingId}/`);
  if (!response.ok) {
    throw new Error('Failed to fetch saying');
  }
  return response.json();
};

// Add more CRUD functions as needed for each resource 